/**
 * Chat storage utility that uses sessionStorage for current tab sessions
 * and localStorage for persistence across days.
 *
 * This allows:
 * - Multiple tabs with independent chat sessions
 * - Session restoration when reopening the browser
 */

const BASE_STORAGE_KEY = 'zeller-pusher-demo';
const CHAT_HISTORY_KEY = 'zeller-demochat-history';

export interface ChatHistoryItem {
  phoneIdentifier: string;
  timestamp: number;
  description: string | null;
  lastMessageCount?: number;
}

export const getClientPhoneIdentifier = (companyIdentifier?: string | null): string | null => {
  if (!companyIdentifier) {
    return null;
  }
  const storageKey = `${BASE_STORAGE_KEY}-${companyIdentifier}`;

  // First, check sessionStorage (tab-specific)
  let clientPhoneIdentifier = sessionStorage.getItem(storageKey);
  if (clientPhoneIdentifier) {
    return clientPhoneIdentifier;
  }
  // If not in sessionStorage, check localStorage (persistent)
  clientPhoneIdentifier = localStorage.getItem(storageKey);

  if (!clientPhoneIdentifier) {
    return null;
  }

  // If found in localStorage, copy to sessionStorage for this tab
  sessionStorage.setItem(storageKey, clientPhoneIdentifier);
  return clientPhoneIdentifier;
};

export const setClientPhoneIdentifier = (companyIdentifier: string, clientPhoneIdentifier: string): void => {
  const storageKey = `${BASE_STORAGE_KEY}-${companyIdentifier}`;

  sessionStorage.setItem(storageKey, clientPhoneIdentifier); // for this tab
  localStorage.setItem(storageKey, clientPhoneIdentifier); // for persistence across browser restarts
};

export const clearClientPhoneIdentifier = (companyIdentifier?: string | null): void => {
  if (!companyIdentifier) {
    return;
  }
  const storageKey = `${BASE_STORAGE_KEY}-${companyIdentifier}`;
  sessionStorage.removeItem(storageKey);
  localStorage.removeItem(storageKey);
};

export const getChatHistory = (companyIdentifier?: string | null): ChatHistoryItem[] => {
  if (!companyIdentifier) {
    return [];
  }
  const storageKey = `${CHAT_HISTORY_KEY}-${companyIdentifier}`;
  const stored = localStorage.getItem(storageKey);

  if (!stored) {
    return [];
  }

  try {
    const history = JSON.parse(stored) as ChatHistoryItem[];
    // Sort by timestamp, newest first
    return history.sort((a, b) => b.timestamp - a.timestamp);
  } catch {
    return [];
  }
};

export const saveChatToHistory = (
  companyIdentifier: string,
  phoneIdentifier: string,
  description?: string | null,
  messageCount?: number
): void => {
  const storageKey = `${CHAT_HISTORY_KEY}-${companyIdentifier}`;
  const existingHistory = getChatHistory(companyIdentifier);

  // Check if chat already exists
  const existingChatIndex = existingHistory.findIndex((chat) => chat.phoneIdentifier === phoneIdentifier);

  if (existingChatIndex >= 0) {
    // Update existing chat
    existingHistory[existingChatIndex] = {
      ...existingHistory[existingChatIndex],
      timestamp: Date.now(),
      ...(description !== undefined && { description }),
      ...(messageCount !== undefined && { lastMessageCount: messageCount }),
    };
  } else {
    // Add new chat to beginning of array
    existingHistory.unshift({
      phoneIdentifier,
      timestamp: Date.now(),
      description: description ?? null,
      lastMessageCount: messageCount ?? 0,
    });
  }

  localStorage.setItem(storageKey, JSON.stringify(existingHistory));
};

export const deleteChatFromHistory = (companyIdentifier: string, phoneIdentifier: string): void => {
  const storageKey = `${CHAT_HISTORY_KEY}-${companyIdentifier}`;
  const existingHistory = getChatHistory(companyIdentifier);

  const filteredHistory = existingHistory.filter((chat) => chat.phoneIdentifier !== phoneIdentifier);

  localStorage.setItem(storageKey, JSON.stringify(filteredHistory));
};

export const updateChatDescription = (
  companyIdentifier: string,
  phoneIdentifier: string,
  description: string
): void => {
  const existingHistory = getChatHistory(companyIdentifier);
  const chatIndex = existingHistory.findIndex((chat) => chat.phoneIdentifier === phoneIdentifier);

  if (chatIndex >= 0) {
    existingHistory[chatIndex].description = description;
    const storageKey = `${CHAT_HISTORY_KEY}-${companyIdentifier}`;
    localStorage.setItem(storageKey, JSON.stringify(existingHistory));
  }
};
